<style scoped>
.info-label {
	font-size: 0.8rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #6c757d;
	margin-bottom: 0.25rem;
}
.own-district {
	border-left: 3px solid var(--bs-success);
	padding-left: 0.75rem;
}
</style>

<template>
	<div>
		<p class="small text-muted">
			Where a district can buy this product. Neither the state nor the federal
			registry records distributors, so a distributor and the districts it
			serves are both stated by a person — by the distributor, or by a district
			that buys from it. Coverage is a direct statement about a district, never
			inferred from geography.
		</p>

		<div v-if="rows.length === 0" class="text-muted small mb-3">
			No distributor recorded for this product yet.
		</div>
		<ul v-else class="list-unstyled mb-3">
			<li
				v-for="row in rows"
				:key="row.distributor_id"
				class="border-top py-2"
				:class="{ 'own-district': row.covers_reader }"
			>
				<div class="d-flex align-items-start gap-2">
					<div class="flex-grow-1">
						<div class="d-flex flex-wrap align-items-center gap-2">
							<a
								v-if="row.website"
								:href="row.website"
								target="_blank"
								rel="noopener noreferrer"
								class="fw-semibold"
							>
								{{ row.name }}
								<i class="bi bi-box-arrow-up-right ms-1 small"></i>
							</a>
							<span v-else class="fw-semibold">{{ row.name }}</span>
							<span v-if="row.covers_reader" class="badge text-bg-success">
								<i class="bi bi-check2-circle me-1"></i>Covers your district
							</span>
							<span
								v-if="row.relationship === 'private_label'"
								class="badge text-bg-info"
								title="This brand is the distributor's own private label"
								>Private label</span
							>
							<span
								v-else
								class="badge text-bg-secondary"
								title="The distributor carries this brand"
								>Distributor</span
							>
							<span class="text-muted small">
								covers {{ row.coverage_count }}
								{{ row.coverage_count === 1 ? "district" : "districts" }}
							</span>
						</div>
						<div v-if="row.relationship_note" class="small text-muted">
							{{ row.relationship_note }}
						</div>

						<!-- The curator's view: every coverage statement with its
						     provenance and audit trail. A district reader never
						     receives these fields. -->
						<ul
							v-if="audit && row.coverage.length > 0"
							class="list-unstyled small text-muted mt-1 mb-0"
						>
							<li v-for="coverage in row.coverage" :key="coverage.id">
								<span class="fw-semibold">{{
									coverage.organization_name
								}}</span>
								· {{ provenanceLabel(coverage.provenance) }}
								<template v-if="coverage.recorded_by">
									· {{ coverage.recorded_by }}
									{{ formatWhen(coverage.recorded_at) }}
								</template>
								<span v-if="coverage.note"> · {{ coverage.note }}</span>
							</li>
						</ul>
						<ul
							v-else-if="!audit && row.covers_reader && row.coverage.length > 0"
							class="list-unstyled small text-muted mt-1 mb-0"
						>
							<li v-for="coverage in row.coverage" :key="coverage.id">
								Your district is covered
								<span v-if="coverage.note">· {{ coverage.note }}</span>
							</li>
						</ul>
					</div>
					<div v-if="audit" class="text-nowrap">
						<RouterLink
							class="btn btn-outline-secondary btn-sm"
							:to="`/_/sudo/distributors/${row.distributor_id}`"
							title="Edit this distributor's brands and coverage"
						>
							<i class="bi bi-pencil"></i>
						</RouterLink>
					</div>
				</div>
			</li>
		</ul>

		<!-- The customer-stated half: a district says which distributor it buys
		     from. The write creates or extends a customer_stated coverage row for
		     the session district, and never another district's. -->
		<form
			v-if="canRecordPurchase && brandID !== null && brandID !== undefined"
			class="border-top pt-3"
			@submit.prevent="recordPurchase"
		>
			<div class="info-label">
				Which distributor does your district buy this from?
			</div>
			<div class="row g-2 align-items-end">
				<div class="col-md-5">
					<select v-model="purchaseChoice" class="form-select form-select-sm">
						<option value="">Select a distributor…</option>
						<option
							v-for="row in rows"
							:key="`purchase-${row.distributor_id}`"
							:value="String(row.distributor_id)"
						>
							{{ row.name }}
						</option>
						<option value="other">Another distributor (type its name)…</option>
					</select>
				</div>
				<div v-if="purchaseChoice === 'other'" class="col-md-4">
					<input
						v-model="purchaseName"
						type="text"
						class="form-control form-control-sm"
						placeholder="Distributor name"
						required
					/>
				</div>
				<div class="col-md-3">
					<input
						v-model="purchaseNote"
						type="text"
						class="form-control form-control-sm"
						placeholder="Note (optional)"
					/>
				</div>
				<div class="col-md-2 d-flex gap-1">
					<button
						type="submit"
						class="btn btn-primary btn-sm"
						:disabled="purchaseSaving || !purchaseReady"
					>
						<span
							v-if="purchaseSaving"
							class="spinner-border spinner-border-sm"
						></span>
						<i v-else class="bi bi-check-lg"></i>
					</button>
				</div>
			</div>
			<div v-if="purchaseError" class="alert alert-danger mt-2 mb-0 py-1 small">
				{{ purchaseError }}
			</div>
			<div
				v-else-if="purchaseSaved"
				class="alert alert-success mt-2 mb-0 py-1 small"
			>
				Recorded. Your district's coverage of this product now names
				{{ purchaseSavedName }}.
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { apiClient } from "@/client";
import type {
	PesticideCoverageProvenance,
	PesticideDistributorPurchase,
	PesticideDistributorPurchaseRequest,
	PesticideWhereToBuy,
} from "@/type/api";

/**
 * WhereToBuy is the ranked "where to buy" list for one brand. The server ranks
 * rows: the reader's own district first and marked, then the rest.
 *
 * `audit` decides whether provenance is shown. The server omits those fields for
 * a district reader, so this only controls the rendering of what a root user
 * receives.
 *
 * Editing a distributor's brands and coverage belongs to the sudo distributor
 * page; this component links there instead of becoming a second place to edit.
 * The one write it owns is the district's own purchase statement.
 */
const props = defineProps<{
	rows: PesticideWhereToBuy[];
	brandID?: number | null;
	audit: boolean;
	canRecordPurchase: boolean;
}>();

const emit = defineEmits<{ changed: [] }>();

const purchaseChoice = ref("");
const purchaseName = ref("");
const purchaseNote = ref("");
const purchaseSaving = ref(false);
const purchaseError = ref("");
const purchaseSaved = ref(false);
const purchaseSavedName = ref("");

const purchaseReady = computed(() => {
	if (purchaseChoice.value === "") return false;
	if (purchaseChoice.value === "other") return purchaseName.value.trim() !== "";
	return true;
});

function provenanceLabel(
	provenance: PesticideCoverageProvenance | undefined,
): string {
	if (provenance === "customer_stated") return "stated by the district";
	if (provenance === "distributor_stated") return "stated by the distributor";
	return "provenance not shown";
}

function formatWhen(value: string | undefined): string {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return ` on ${date.toLocaleDateString()}`;
}

async function recordPurchase(): Promise<void> {
	if (!props.brandID || !purchaseReady.value) return;
	purchaseSaving.value = true;
	purchaseError.value = "";
	purchaseSaved.value = false;
	const request: PesticideDistributorPurchaseRequest = {
		distributor_id:
			purchaseChoice.value === "other" ? null : Number(purchaseChoice.value),
		distributor_name:
			purchaseChoice.value === "other" ? purchaseName.value.trim() : null,
		note: purchaseNote.value.trim() === "" ? null : purchaseNote.value.trim(),
	};
	try {
		const result = await apiClient.JSONPost<PesticideDistributorPurchase>(
			`/api/pesticide-catalog/brand/${props.brandID}/purchase`,
			request,
		);
		purchaseSaved.value = true;
		purchaseSavedName.value = result.distributor_name;
		purchaseChoice.value = "";
		purchaseName.value = "";
		purchaseNote.value = "";
		emit("changed");
	} catch (e) {
		purchaseError.value = e instanceof Error ? e.message : String(e);
	} finally {
		purchaseSaving.value = false;
	}
}
</script>
